import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { useGetOrganizationsQuery } from '../../api/organizationApi';
import {
  useGetOrgUsersQuery,
  useSetUserActiveMutation,
  useDeleteUserMutation,
} from '../../api/peopleApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import useAutoSelectFirst from '../../hooks/useAutoSelectFirst';
import StatusDots from '../../components/StatusDots/StatusDots';
import Badge from '../../components/Badge/Badge';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import table from '../../styles/shared/table.module.css';

const ROLE_COLORS = {
  manager: { background: 'var(--color-badge-info-bg)', color: 'var(--color-primary)' },
  host: { background: 'var(--color-badge-warn-bg)', color: 'var(--color-badge-warn-text)' },
};

export default function UsersPage() {
  const { data: organizations, isLoading: loadingOrgs } = useGetOrganizationsQuery();
  const [organizationId, setOrganizationId] = useState('');
  const navigate = useNavigate();
  const showToast = useToast();

  useAutoSelectFirst(organizations, organizationId, setOrganizationId);

  const {
    data: users,
    isLoading: loadingUsers,
    isError: usersError,
    error: usersLoadError,
  } = useGetOrgUsersQuery({ organizationId }, { skip: !organizationId });

  const [setActive] = useSetUserActiveMutation();
  const [deleteUser] = useDeleteUserMutation();

  useEffect(() => {
    if (usersError) {
      showToast('error', 'Users', errMsg(usersLoadError, 'Could not load users.'));
    }
  }, [usersError, usersLoadError, showToast]);

  const tooltipRef = useRef(null);
  useEffect(() => {
    tooltipRef.current?.updateTargetEvents();
  }, [users]);

  async function handleToggleActive(user) {
    try {
      await setActive({ id: user.id, organizationId, isActive: !user.is_active }).unwrap();
      showToast('success', 'Users', user.is_active ? 'User disabled.' : 'User enabled.');
    } catch (err) {
      showToast('error', 'Users', errMsg(err, 'Could not update user.'));
    }
  }

  async function handleDelete(user) {
    try {
      await deleteUser({ id: user.id, organizationId }).unwrap();
      showToast('success', 'Users', 'User deleted.');
    } catch (err) {
      showToast('error', 'Users', errMsg(err, 'Could not delete user.'));
    }
  }

  const goEdit = (user) => navigate(`${user.id}/edit`);
  const goPassword = (user) => navigate(`${user.id}/password`);

  const actionBodyTemplate = (user) => (
    <div className={table.actions}>
      <Button icon="pi pi-pencil" text className={table.iconNeutral} onClick={() => goEdit(user)} />
      <Button icon="pi pi-key" text className={table.iconNeutral} onClick={() => goPassword(user)} />
      <Button
        icon={`pi ${user.is_active ? 'pi-ban' : 'pi-check'}`}
        text
        className={user.is_active ? table.iconDisable : table.iconSuccess}
        onClick={() => handleToggleActive(user)}
      />
      <Button
        icon="pi pi-trash"
        text
        className={table.iconDanger}
        onClick={() => handleDelete(user)}
      />
    </div>
  );

  if (loadingOrgs) return <p className="textMuted">Loading…</p>;
  if (!organizations?.length)
    return (
      <div className={s.page}>
        <h1>Users</h1>
        <p className={`${s.subtitle} textMuted`}>
          Create an organization first, then add managers and hosts to it.
        </p>
      </div>
    );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Users</h1>
          <p className={`${s.subtitle} textMuted`}>
            Managers and hosts for an organization.
          </p>
        </div>
        <Button
          label="Add user"
          icon="pi pi-plus"
          className={buttons.btnPrimary}
          onClick={() => navigate('create', { state: { organizationId } })}
        />
      </div>

      <div className={`field ${s.orgSelect}`}>
        <label htmlFor="organization">Organization</label>
        <Dropdown
          inputId="organization"
          value={organizationId}
          onChange={(e) => setOrganizationId(e.value)}
          options={organizations.map((c) => ({ label: `${c.name} (${c.slug})`, value: c.id }))}
          className="w-full"
        />
      </div>

      <div className={table.tableWrapper}>
        <Tooltip ref={tooltipRef} target={`.${table.statusDot}`} position="left" />
        <DataTable
          value={users ?? []}
          loading={loadingUsers}
          emptyMessage="No users in this organization yet. Add one above."
          responsiveLayout="stack"
        >
          <Column
            field="name"
            header="Name"
            className={table.contactCol}
            body={(u) => <div className={table.cellInsider}>{u.name}</div>}
          />
          <Column
            field="email"
            header="Email"
            className={`${s.mutedCell} ${table.contactCol}`}
            body={(u) => <div className={table.cellInsider}>{u.email}</div>}
          />
          <Column
            field="role"
            header="Role"
            className={table.contactCol}
            body={(u) => (
              <div className={table.cellInsider}>
                <Badge {...ROLE_COLORS[u.role]}>{u.role}</Badge>
              </div>
            )}
          />
          <Column
            header="Status"
            className={table.statusCol}
            body={(u) => (
              <div className={table.cellInsider}>
                <div className={`${table.statusBadgesDesktop} ${u.is_active ? s.statusOk : s.statusDisabled}`}>
                  {u.is_active ? 'active' : 'disabled'}
                </div>
                <StatusDots
                  items={[
                    {
                      label: u.is_active ? 'active' : 'disabled',
                      color: u.is_active ? 'var(--color-success)' : 'var(--color-error)',
                    },
                  ]}
                />
              </div>
            )}
          />
          <Column
            header="Actions"
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: '328px' }}
          />
        </DataTable>
      </div>
    </div>
  );
}
